import type { TpvOfflineAuthority } from './authority';
import type { TpvOfflineKit } from './kit';

/**
 * docs/decisions/2026-10-28-tpv-sin-conexion.md · which door the shell draws at boot on a TPV, as pure data. It is
 * what the Panel's entry holds of the offline door: the device is read (`door.ts`) and the door drawn
 * (`./door-stage.tsx`, in the TPV's chunk) only where they are needed.
 */

/** What the door opens over: the kit and the authority this device holds for the door's restaurant. */
type TpvDoorDevice = Readonly<{ kit: TpvOfflineKit; authority: TpvOfflineAuthority }>;

export type TpvOfflineDoor = Readonly<{
  restaurantId: string;
  restaurantName: string;
  /** The authority's person: every entry written behind the door is theirs. */
  operator: string;
  /** The kit and the authority read at boot, carried to the door's TPV so the device is not read twice. */
  device: TpvDoorDevice;
  /** When the outage began on this tablet, or null when none is recorded. */
  outageSince: string | null;
  /** Journal entries not yet uploaded. */
  pendingCount: number;
}>;

/** The journal entries of one restaurant that have not reached the Service. */
export type TpvUnsentJournal = Readonly<{ restaurantId: string; pendingCount: number }>;

/** What this device holds at boot: the door it offers, and the entries that wait whatever the door. */
export type TpvDeviceBoot = Readonly<{ door: TpvOfflineDoor | null; unsent: readonly TpvUnsentJournal[] }>;

export const TPV_NO_DEVICE_BOOT: TpvDeviceBoot = Object.freeze({ door: null, unsent: Object.freeze([]) });

/** What the shell knows at boot. */
export type TpvBootDoorInput = Readonly<{
  device: TpvDeviceBoot;
  /**
   * The identity runtime's stage: `restoring` (still reading), `signed_out` (the login door), `no-session` (the
   * tab holds a bearer whose session could not be confirmed: the `cannot-open` stage), `ready` (a session of `sessionRestaurantId`) or `other`.
   */
  identity: 'restoring' | 'signed_out' | 'no-session' | 'ready' | 'other';
  sessionRestaurantId?: string | null;
  /** The browser says it is offline. */
  navigatorOnline: boolean;
  /** The public clock at boot: answered, failed twice 5 s apart, or not settled yet. */
  clock: 'answered' | 'failed' | 'unknown';
}>;

export type TpvBootDoor =
  | Readonly<{ kind: 'none' }>
  /** The TPV alone over the kit, offline («Modo sin conexión»). */
  | Readonly<{ kind: 'offline-door'; door: TpvOfflineDoor }>
  /** The login door with the band «Hay {n} operaciones sin subir…». */
  | Readonly<{ kind: 'login-band'; pendingCount: number }>
  /** A session of another restaurant while this tablet holds unsent entries of `restaurantId`. */
  | Readonly<{ kind: 'other-restaurant'; restaurantId: string }>;

/**
 * Whether the Service is unreachable from here: the browser is offline, or the public clock failed twice, 5 s apart
 * (docs/decisions/2026-10-31-tpv-offline-without-a-caja-tablet.md: one slow answer never forces an online TPV offline).
 */
const tpvDeviceUnreachable = (input: Pick<TpvBootDoorInput, 'navigatorOnline' | 'clock'>): boolean =>
  !input.navigatorOnline || input.clock === 'failed';

/**
 * Which door the shell draws (`app/runtime-boundary.tsx`): before the `cannot-open` stage, the offline door when it
 * is available (this tab holds the session that made it, `door.ts`) and the Service unreachable; signed out there is
 * no session, so no door. The login door's band when the Service is reachable and entries wait, whether or not the
 * door is still open; a ready session of another restaurant learns that this tablet holds another restaurant's
 * entries.
 */
export function tpvBootDoor(input: TpvBootDoorInput): TpvBootDoor {
  const { door, unsent } = input.device;
  const unreachable = tpvDeviceUnreachable(input);
  if (door !== null && input.identity === 'no-session' && unreachable) return { kind: 'offline-door', door };
  const pendingCount = unsent.reduce((sum, entry) => sum + entry.pendingCount, 0);
  if (input.identity === 'signed_out' && !unreachable && pendingCount > 0) return { kind: 'login-band', pendingCount };
  const other = input.identity === 'ready' && input.sessionRestaurantId != null
    ? unsent.find((entry) => entry.restaurantId !== input.sessionRestaurantId && entry.pendingCount > 0) : undefined;
  return other === undefined ? { kind: 'none' } : { kind: 'other-restaurant', restaurantId: other.restaurantId };
}
